import { useRef } from "react";
import { motion, useScroll, useSpring } from "framer-motion";
import { useLang, loc, UI, TIMELINE } from "@/lib/content";

export default function Timeline() {
  const { lang } = useLang();
  const ref = useRef(null);
  const { scrollYProgress } = useScroll({ target: ref, offset: ["start 0.75", "end 0.6"] });
  const progress = useSpring(scrollYProgress, { stiffness: 60, damping: 20 });

  return (
    <section id="camino" ref={ref} className="bg-paper py-24">
      <div className="mx-auto grid max-w-7xl gap-14 px-5 sm:px-8 lg:grid-cols-[0.8fr_1.2fr]">
        <div className="lg:sticky lg:top-28 lg:self-start">
          <motion.div
            initial={{ opacity: 0, y: 24 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true, amount: 0.3 }}
            transition={{ duration: 0.7 }}
          >
            <p className="flex items-center gap-3 font-mono text-[11px] font-semibold uppercase tracking-[0.22em] text-heritage">
              <span className="h-px w-8 bg-heritage" /> {loc(UI.timelineHeader.overline, lang)}
            </p>
            <h2 className="mt-4 font-display text-4xl font-semibold tracking-tight text-navy sm:text-5xl">
              {loc(UI.timelineHeader.title, lang)}
              <span className="italic text-heritage">{loc(UI.timelineHeader.titleAccent, lang)}</span>
            </h2>
            <p className="mt-4 max-w-md text-inkbody">{loc(UI.timelineHeader.intro, lang)}</p>
          </motion.div>
        </div>

        <ol className="relative space-y-10 border-l-2 border-line pl-8" data-testid="timeline-steps">
          <motion.span
            style={{ scaleY: progress }}
            className="absolute left-[-2px] top-0 h-full w-[2px] origin-top bg-heritage"
          />
          {TIMELINE.map((step, i) => (
            <motion.li
              key={i}
              initial={{ opacity: 0, x: 28 }}
              whileInView={{ opacity: 1, x: 0 }}
              viewport={{ once: true, amount: 0.4 }}
              transition={{ duration: 0.55, delay: i * 0.05 }}
              data-testid={`timeline-step-${i + 1}`}
              className="relative"
            >
              <span
                className={`absolute -left-[2.85rem] flex h-9 w-9 items-center justify-center rounded-full border-2 text-xs font-bold ${
                  i === TIMELINE.length - 1 ? "border-heritage bg-heritage text-paper" : "border-navy bg-paper text-navy"
                }`}
              >
                {i + 1}
              </span>
              <p className="font-mono text-[10px] font-semibold uppercase tracking-[0.22em] text-mutedtext">
                {loc(UI.timelineHeader.step, lang)} {i + 1}
              </p>
              <h3 className="mt-1 font-display text-2xl font-semibold text-navy">{loc(step.title, lang)}</h3>
              <p className="mt-1.5 max-w-lg text-sm leading-relaxed text-inkbody">{loc(step.desc, lang)}</p>
            </motion.li>
          ))}
        </ol>
      </div>
    </section>
  );
}
